import React, { SyntheticEvent, useState } from "react";

// material ui
import {
  Chip,
  Box,
  Autocomplete,
  TextField,
  Typography,
  ListItem,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/system";
import UpgradeNotification from "components/display/upgradeNotification";
import isAutoCompleteOptionDisabled from "./isAutoCompleteOptionDisabled";

type AutocompleteOption = {
  label?: string;
  disabled?: boolean;
  beingUpdated?: boolean;
  dataElType?: string;
  [key: string]: any;
};

type AutocompleteProps = {
  title: string;
  label: string;
  disabled?: boolean;
  value?: AutocompleteOption[] | AutocompleteOption | null;
  id?: string;
  helperText?: string;
  optionKeyField?: string;
  disableClearable?: boolean;
  filterSelectedOptions?: boolean;
  data: AutocompleteOption[];
  groupByField?: string;
  groupByFieldToLabel?: { [key: string]: string };
  renderOptionField: string;
  renderTagField: string;
  showTitle: boolean;
  showLabel: boolean;
  noOptionsText?: string;
  condensed?: boolean;
  multiple?: boolean;
  mobile?: boolean;
  noWrap?: boolean;
  upgradeNotification?: {
    variant: "info" | "warning" | "error";
    text: string;
    dismissable: boolean;
  };
  changeHandler: (e: SyntheticEvent<Element, Event>, v: any) => void;
};

interface TypeLegendProps {
  variant: string | undefined;
}

const TypeLegend = styled("div")<TypeLegendProps>(({ theme, variant }) => {
  let bgColor;
  let show;
  let wide;
  switch (variant) {
    case "map":
      bgColor = theme.palette.success.main;
      show = true;
      break;
    case "extMap":
      bgColor = theme.palette.secondary.main;
      show = true;
      break;
    case "estate":
      bgColor = "black";
      show = true;
      wide = true;
      break;
    case "extEstate":
      bgColor = theme.palette.secondary.main;
      show = true;
      wide = true;
      break;
    case "project":
      bgColor = theme.palette.info.main;
      show = true;
      break;
    case "archivedProject":
      bgColor = theme.palette.secondary.main;
      show = true;
      break;
    case "allInventories":
      bgColor = "black";
      show = true;
      wide = true;
      break;
    case "selectAllOptions":
      bgColor = "black";
      show = true;
      wide = true;
      break;
    default:
      bgColor = theme.palette.primary.lighter;
      show = false;
  }

  if (!show) return null;

  return {
    //minWidth: 8,
    width: wide ? 12 : 8,
    height: 20,
    backgroundColor: bgColor,
    borderRadius: 2,
    marginRight: 8,
  };
});

// ==============================|| AUTOCOMPLETE - GROUPED ||============================== //

const AutoCompleteGrouped = ({
  title,
  label,
  id = "autocomplete-grouped",
  helperText,
  optionKeyField,
  disableClearable,
  filterSelectedOptions = true,
  value,
  data,
  groupByField,
  groupByFieldToLabel,
  renderOptionField,
  renderTagField,
  showTitle,
  showLabel,
  noOptionsText,
  condensed,
  multiple,
  noWrap,
  mobile,
  disabled,
  upgradeNotification,
  changeHandler,
}: AutocompleteProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  return (
    <>
      {showTitle && (
        <Typography variant="h5" sx={{ mb: 1 }}>
          {title}
        </Typography>
      )}

      <Autocomplete<AutocompleteOption, boolean, boolean, false>
        multiple={multiple}
        fullWidth
        disabled={disabled}
        id={id}
        disableClearable={disableClearable}
        value={value}
        options={data}
        groupBy={
          groupByField
            ? (option) =>
                groupByFieldToLabel
                  ? groupByFieldToLabel[option[groupByField]] ||
                    option[groupByField]
                  : option[groupByField]
            : undefined
        }
        noOptionsText={noOptionsText ? noOptionsText : null}
        getOptionLabel={(option) => option.label ?? ""}
        getOptionDisabled={isAutoCompleteOptionDisabled}
        isOptionEqualToValue={(option, value) =>
          option[optionKeyField ?? "label"] === value[optionKeyField ?? "label"]
        }
        defaultValue={data}
        onChange={(e, v) => changeHandler(e, v)}
        filterSelectedOptions={filterSelectedOptions}
        ListboxProps={{
          sx: {
            "& .MuiAutocomplete-groupLabel": {
              lineHeight: (theme) => theme.spacing(3),
            },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={label}
            helperText={helperText}
            inputProps={{ ...params.inputProps, "aria-label": title }}
            label={showLabel ? title : null}
            sx={{ "& .MuiFormLabel-root": { fontSize: 14 } }}
            color="secondary"
          />
        )}
        renderOption={(props, option) => {
          const loading = option.beingUpdated;
          const { key, ...optionProps } = props;
          return (
            <ListItem
              key={optionKeyField ? option[optionKeyField] : key}
              {...optionProps}
              sx={{
                "&&:hover": {
                  backgroundColor: "primary.lighter",
                },
              }}
            >
              <TypeLegend variant={option.dataElType} />
              {option[renderOptionField]}
              {loading && (
                <CircularProgress sx={{ ml: 1 }} size={16} color="inherit" />
              )}
            </ListItem>
          );
        }}
        ListboxComponent={React.forwardRef(
          ({ children, ...props }: any, ref) => (
            <Box {...props} ref={ref}>
              {upgradeNotification && (
                <Box sx={{ p: 1 }}>
                  <UpgradeNotification
                    variant={upgradeNotification.variant}
                    text={upgradeNotification.text}
                    dismissable={upgradeNotification.dismissable}
                  />
                </Box>
              )}
              {children}
            </Box>
          )
        )}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            let chipColor;
            let color;
            switch (option.dataElType) {
              case "map":
                chipColor = "success.main";
                color = "white";
                break;
              case "extMap":
                chipColor = "secondary.main";
                color = "white";
                break;
              case "estate":
                chipColor = "black";
                color = "white";
                break;
              case "extEstate":
                chipColor = "secondary.main";
                color = "white";
                break;
              case "project":
                chipColor = "info.main";
                color = "white";
                break;
              case "archivedProject":
                chipColor = "secondary.main";
                color = "white";
                break;
              case "allInventories":
                chipColor = "black";
                color = "white";
                break;
              case "selectAllOptions":
                chipColor = "black";
                color = "white";
                break;
              default:
                chipColor = "primary.lighter";
                color = "primary.dark";
            }

            let top = 3;
            if (mobile) {
              top = 2;
            }
            const shouldRenderChip = index < top || isFocused || isHovered;
            if (shouldRenderChip) {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option[renderTagField]}
                  {...tagProps}
                  disabled={disabled}
                  sx={{
                    minWidth: "90px",
                    margin: condensed ? "2px" : "4px",
                    bgcolor: chipColor,
                    color: color,
                    borderRadius: 1,
                    height: condensed ? 24 : 28,
                    pl: condensed ? 1.0 : 1.5,
                    pr: condensed ? 1.0 : 1.5,
                    lineHeight: condensed ? "24px" : "28px",
                    whiteSpace: "nowrap", // Prevent wrapping inside the tag
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    "&:focus": {
                      whiteSpace: "normal", // Allow wrapping when the tag is focused
                    },
                    marginBottom: "4px", // Ensures spacing between wrapped chips
                    "&:last-child": {
                      marginBottom: "0px", // Ensures no extra space at the bottom
                    },
                    "& .MuiChip-label": {
                      paddingLeft: condensed ? 0.5 : 0,
                      paddingRight: 0,
                    },
                    "& .MuiSvgIcon-root": {
                      color: color,
                      "&:hover": {
                        color: color,
                      },
                      ml: 1,
                      mr: condensed ? -0.25 : -0.75,
                      height: condensed ? 18 : null,
                    },
                  }}
                />
              );
            } else if (index === top && !isFocused && !isHovered) {
              return (
                <Chip
                  key={index}
                  label={`+${tagValue.length - top}`}
                  sx={{
                    margin: condensed ? "2px" : "4px",
                    bgcolor: chipColor,
                    color: color,
                    borderRadius: 1,
                    height: condensed ? 24 : 28,
                    lineHeight: condensed ? "24px" : "28px",
                  }}
                />
              );
            } else {
              return null;
            }
          })
        }
        sx={{
          // Root style
          "& .MuiOutlinedInput-root": {
            px: 0.5,
            py: 0.4,
            minHeight: condensed ? 36 : 41,
            flexWrap: "nowrap", // Prevent wrapping by default
            overflow: "hidden",
            alignItems: "center", // Vertically center the chips
            "&:hover, &:focus-within": {
              flexWrap: noWrap ? "nowrap" : "wrap", // Allow wrapping when hovered or focused
            },
          },
          "& .MuiInputBase-input": {
            fontSize: 14,
            height: 15,
          },
          "& .MuiFormLabel-root": {
            marginTop: "-5px",
          },
          "& .MuiInputLabel-shrink": {
            marginTop: "0px",
          },
        }}
      />
    </>
  );
};

export default AutoCompleteGrouped;
